import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import styled from "styled-components";

import { AppNavbar, ErrorScreen } from "@components";
import { useUser } from "@hooks";

import { Footer } from "@pages/appLanding/components/Footer";
import { SmartRedirect as ManageRedirect } from "@pages/dashboard/routes/ManageRedirect";
import { SmartRedirect } from "@pages/editor";
import { GitLabSemgrepScreen } from "@pages/forGitLab/GitLabSemgrepLanding";
import { IssueSentimentResponseScreen } from "@pages/issueSentimentResponse/IssueSentimentResponseScreen";
import { LearnHome } from "@pages/learn";
import { OrganizationInvitePanel } from "@pages/organizationInvite/OrganizationInvitePanel";
import {
  SlackOAuthCallbackScreen,
  SlackOAuthErrorScreen,
} from "@pages/settings/components";
import { PublicCreateSSODeployment } from "@pages/ssoSetup/PublicCreateSSODeployment";
import { SSOSignIn } from "@pages/ssoSignIn";
import { SsoErrorScreen } from "@pages/ssoSignIn/components";
import { UsageLimitsScreen } from "@pages/usageLimits/UsageLimitsScreen";

import { AuthedRoute } from "./AuthedRoute";
import { PlaygroundRouter } from "./PlaygroundRouter";

const CheatsheetEmbed = React.lazy(() =>
  import("@pages/embed/CheatsheetEmbed").then((module) => ({
    default: module.CheatsheetEmbed,
  }))
);
const RuleWizardScreen = React.lazy(() =>
  import("@pages/wizard").then((module) => ({
    default: module.RuleWizardScreen,
  }))
);
const LoginScreen = React.lazy(() =>
  import("@pages/login/Login").then((module) => ({
    default: module.Login,
  }))
);

// Semgrep Cloud

const DashboardApp = React.lazy(() => import("@pages/dashboard/DashboardApp"));

// End Semgrep Cloud

const EditorWidget = React.lazy(() =>
  import("src/components/EditorWidget/EditorWidget").then((module) => ({
    default: module.EditorWidget,
  }))
);
const GithubInstallRedirect = React.lazy(() =>
  import("@pages/login/components/GithubInstallRedirect").then((module) => ({
    default: module.GithubInstallRedirect,
  }))
);
const GitlabOrganizationsScreen = React.lazy(() =>
  import("@pages/gitLabOrganizations/GitlabOrganizations").then((module) => ({
    default: module.GitlabOrganizations,
  }))
);
const OAuthCallbackScreen = React.lazy(() =>
  import("@pages/login/components/OAuthCallbackScreen").then((module) => ({
    default: module.OAuthCallbackScreen,
  }))
);
const SamlFinishScreen = React.lazy(() =>
  import("@pages/login/components/SamlFinishScreen").then((module) => ({
    default: module.SamlFinishScreen,
  }))
);
const LoginOAuthRedirect = React.lazy(() =>
  import("@pages/login/components/LoginOAuthRedirect").then((module) => ({
    default: module.LoginOAuthRedirect,
  }))
);

const Admin = React.lazy(() =>
  import("@pages/admin").then((module) => ({ default: module.Admin }))
);

const Learn = React.lazy(() =>
  import("@pages/learn").then((module) => ({ default: module.Learn }))
);

const AppLanding = React.lazy(() =>
  import("@pages/appLanding/AppLanding").then((module) => ({
    default: module.AppLanding,
  }))
);

const ToggleExperimentalFeatures = React.lazy(() =>
  import("@pages/experimentalFeatures/ToggleExperimentalFeatures").then(
    (module) => ({ default: module.ToggleExperimentalFeatures })
  )
);

const DeepSemgrep = React.lazy(() => import("@pages/contactUs/DeepSemgrep"));

const RegistryScreen = React.lazy(() =>
  import("@pages/registry").then((module) => ({
    default: module.RegistryScreen,
  }))
);

const LoginPopup = React.lazy(() =>
  import("@pages/login/components/LoginPopup").then((module) => ({
    default: module.LoginPopup,
  }))
);
const LoginPopupConfirm = React.lazy(() =>
  import("@pages/login/components/LoginPopupConfirm").then((module) => ({
    default: module.LoginPopupConfirm,
  }))
);

const AppContainer = styled.main`
  padding-top: var(--global-spacing-navbar-height);
`;

const RegistryAppContainer = styled(AppContainer)`
  background: #ffffff;
  min-height: 100vh;
`;

export const AppRoutes: React.FC = () => {
  const user = useUser();
  const queryString = window.location.search;
  const showInviteScreen = user && user.flags?.is_invite_pending;

  return (
    <Switch>
      <Route path={["/", "/products/semgrep-app"]} exact>
        <AppNavbar />
        <AppLanding />
      </Route>
      <Route path="/login" exact>
        <AppNavbar showLogin={false} />
        <LoginScreen />
      </Route>
      <Route
        path="/experimental_features/:toggleChoice(enabled|disabled)"
        component={ToggleExperimentalFeatures}
        exact
      />
      <Route path="/deep-semgrep-beta" exact>
        <AppNavbar />
        <DeepSemgrep />
      </Route>
      <Route path="/for/gitlab" exact>
        <AppNavbar />
        <GitLabSemgrepScreen />
      </Route>
      <Route
        path="/oauth/login/:authProviderId"
        component={LoginOAuthRedirect}
        exact
      />
      <Route
        exact
        path="/oauth/temp/slack"
        component={SlackOAuthCallbackScreen}
      />
      <Route
        exact
        path="/oauth/slack-error"
        component={SlackOAuthErrorScreen}
      />
      <Route
        path="/oauth/callback/:authProviderId"
        component={OAuthCallbackScreen}
        exact
      />
      <Route path="/auth/saml/finish" component={SamlFinishScreen} exact />
      <Route path="/github/login" exact>
        <Redirect to="/oauth/login/github" />
      </Route>
      <Route path="/github/install" exact>
        <GithubInstallRedirect />
      </Route>
      <Route path="/gitlab/organizations" exact>
        <GitlabOrganizationsScreen />
      </Route>
      <Route path="/callback" exact>
        <Redirect to="/oauth/callback/github" />
      </Route>
      <Route path="/embed/cheatsheet" exact component={CheatsheetEmbed} />
      <Route path="/embed/editor" exact component={EditorWidget} />
      <Route path="/registry">
        <Redirect to="/r" />
      </Route>
      <Redirect from="/packs" to="/r" exact />
      <Redirect from="/rulesets" to="/r" exact />
      <Route path="/r" exact>
        <RegistryAppContainer>
          <AppNavbar />
          <RegistryScreen defaultTabId="rules" />
        </RegistryAppContainer>
      </Route>
      <Route path="/r/:registryId">
        <RegistryAppContainer>
          <AppNavbar />
          <RegistryScreen defaultTabId="rules" />
        </RegistryAppContainer>
      </Route>
      <Route path="/explore" exact>
        <RegistryAppContainer>
          <AppNavbar />
          <RegistryScreen defaultTabId="explore" />
        </RegistryAppContainer>
      </Route>
      <Route path="/p/:packId" exact>
        <RegistryAppContainer>
          <AppNavbar />
          <RegistryScreen defaultTabId="one-pack" />
        </RegistryAppContainer>
      </Route>
      <Route path="/login-popup" exact>
        <LoginPopup />
      </Route>
      <Route path="/login-popup-confirm" exact>
        <LoginPopupConfirm />
      </Route>
      <Route path="/login-sso" exact>
        <SSOSignIn />
      </Route>
      <Route path="/welcome/survey">
        <Redirect to="/orgs/:orgName" />
      </Route>
      <AuthedRoute path="/orgs/:orgName">
        <>
          {showInviteScreen ? (
            <AppContainer>
              <AppNavbar />
              <OrganizationInvitePanel />
            </AppContainer>
          ) : (
            <DashboardApp />
          )}
        </>
      </AuthedRoute>
      <Route path="/issue-sentiment-response" exact>
        <AppNavbar />
        <IssueSentimentResponseScreen />
      </Route>
      <Route path="/faq-usage-limits" exact>
        <AppNavbar />
        <UsageLimitsScreen />
      </Route>
      <Route path={["/playground", "/s"]}>
        <PlaygroundRouter />
      </Route>
      <Route path="/create-sso-deployment" exact>
        <AppNavbar />
        <PublicCreateSSODeployment />
      </Route>
      <Route path="/:path?">
        <AppContainer>
          <AppNavbar />
          <Switch>
            <AuthedRoute path="/manage/:sections*" component={ManageRedirect} />
            {user?.roles?.admin === true ? (
              <Route path="/admin" exact component={Admin} />
            ) : null}
            <Route path="/fatal-error" component={ErrorScreen} exact />
            <Route path="/sso-error" component={SsoErrorScreen} exact />
            <Redirect from="/editor" to={`/playground${queryString}`} exact />
            <Route path="/wizard" exact component={RuleWizardScreen} />
            <Route path="/learn" exact component={LearnHome} />
            {/* Legacy support for links to e.g. /learn/3 */}
            <Route path="/learn/:step" exact component={LearnHome} />
            <Route path="/learn/:tutorial/:step" component={Learn} />
            <Route path="/:snippetFqname" component={SmartRedirect} exact />
          </Switch>
          <Footer />
        </AppContainer>
      </Route>
    </Switch>
  );
};
